import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { BlogBlock } from '@/modules/marketing/blog/blog-markdown';

/**
 * A post's body, drawn from its blocks.
 *
 * ## Written to be read, not skimmed
 *
 * The documentation sets its text small and muted because people look things up in it. A post is
 * read start to finish, so the body is larger, higher in contrast and set with generous line
 * height, and a code block is a panel of its own rather than a grey strip.
 *
 * ## Links
 *
 * A path on this site becomes a router link, so moving between posts and the product pages does
 * not reload the app. Anything else opens in a new tab, and says where it is going by being the
 * kind of link a person chose to write.
 */

const LINK = 'text-primary underline decoration-primary/30 underline-offset-4 transition-colors hover:decoration-primary';

function Inline({ text, keyPrefix }: { text: string; keyPrefix: string }) {
  const nodes: ReactNode[] = [];
  const pattern = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0;
  let count = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }

    const token = match[0];
    const key = `${keyPrefix}-${count++}`;

    if (token.startsWith('`')) {
      nodes.push(
        <code key={key} className="bg-muted text-foreground rounded px-1.5 py-0.5 font-mono text-[0.86em] break-words">
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('**')) {
      nodes.push(
        <strong key={key} className="text-foreground font-semibold">
          {token.slice(2, -2)}
        </strong>,
      );
    } else {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token);
      const label = link?.[1] ?? token;
      const href = link?.[2] ?? '#';

      nodes.push(
        /^https?:\/\//.test(href) ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer" className={LINK}>
            {label}
          </a>
        ) : href.startsWith('#') ? (
          <a key={key} href={href} className={LINK}>
            {label}
          </a>
        ) : (
          <Link key={key} to={href} className={LINK}>
            {label}
          </Link>
        ),
      );
    }

    last = match.index + token.length;
  }

  if (last < text.length) {
    nodes.push(text.slice(last));
  }

  return <>{nodes}</>;
}

export function BlogBody({ blocks }: { blocks: BlogBlock[] }) {
  return (
    <div className="text-foreground/85 text-[17px] leading-8">
      {blocks.map((block, index) => {
        const key = `b${index}`;

        switch (block.type) {
          case 'heading':
            return block.level === 2 ? (
              <h2
                key={key}
                id={block.id}
                className="text-foreground mt-14 scroll-mt-24 text-2xl font-semibold tracking-tight text-balance"
              >
                {block.text}
              </h2>
            ) : (
              <h3 key={key} id={block.id} className="text-foreground mt-9 scroll-mt-24 text-xl font-semibold tracking-tight">
                {block.text}
              </h3>
            );

          case 'code':
            return (
              <div key={key} className="border-border bg-muted/40 mt-6 overflow-hidden rounded-xl border">
                {block.language !== '' && (
                  <p className="border-border text-muted-foreground border-b px-4 py-1.5 font-mono text-xs">
                    {block.language}
                  </p>
                )}
                <pre className="text-foreground overflow-x-auto px-4 py-3.5 font-mono text-[13.5px] leading-6">
                  <code>{block.text}</code>
                </pre>
              </div>
            );

          case 'list': {
            const Tag = block.ordered ? 'ol' : 'ul';

            return (
              <Tag
                key={key}
                className={`mt-5 space-y-2.5 pl-6 marker:text-muted-foreground ${block.ordered ? 'list-decimal' : 'list-disc'}`}
              >
                {block.items.map((item, itemIndex) => (
                  <li key={`${key}-${itemIndex}`} className="pl-1.5">
                    <Inline text={item} keyPrefix={`${key}-${itemIndex}`} />
                  </li>
                ))}
              </Tag>
            );
          }

          case 'callout':
            return (
              <aside key={key} className="border-primary/25 bg-primary/5 mt-7 rounded-xl border px-5 py-4 text-[16px] leading-7">
                {block.paragraphs.map((paragraph, paragraphIndex) => (
                  <p key={`${key}-${paragraphIndex}`} className={paragraphIndex === 0 ? '' : 'mt-3'}>
                    <Inline text={paragraph} keyPrefix={`${key}-${paragraphIndex}`} />
                  </p>
                ))}
              </aside>
            );

          case 'table':
            return (
              <div key={key} className="border-border mt-7 overflow-x-auto rounded-xl border">
                <table className="w-full min-w-[34rem] border-collapse text-left text-[15px] leading-6">
                  <thead>
                    <tr className="bg-muted/50">
                      {block.header.map((cell, cellIndex) => (
                        <th key={`${key}-h${cellIndex}`} scope="col" className="text-foreground px-4 py-3 font-semibold">
                          <Inline text={cell} keyPrefix={`${key}-h${cellIndex}`} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, rowIndex) => (
                      <tr key={`${key}-r${rowIndex}`} className="border-border/60 border-t">
                        {row.map((cell, cellIndex) => (
                          <td key={`${key}-r${rowIndex}-${cellIndex}`} className="px-4 py-3 align-top">
                            <Inline text={cell} keyPrefix={`${key}-r${rowIndex}-${cellIndex}`} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );

          case 'figure':
            return (
              <figure key={key} className="mt-9">
                <img
                  src={block.src}
                  alt={block.alt}
                  loading="lazy"
                  decoding="async"
                  className="border-border/60 w-full rounded-xl border"
                />
                {block.caption !== '' && (
                  <figcaption className="text-muted-foreground mt-3 text-center text-sm leading-6">{block.caption}</figcaption>
                )}
              </figure>
            );

          case 'paragraph':
          default:
            return (
              <p key={key} className="mt-5">
                <Inline text={block.text} keyPrefix={key} />
              </p>
            );
        }
      })}
    </div>
  );
}
